import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { QrCode, Camera, Printer, Shield, Heart, ArrowRight, CheckCircle } from "lucide-react";
import { MetaTags, getHowToSchema } from "@/components/MetaTags";
import { SocialShare } from "@/components/SocialShare";

const HowItWorks = () => {
  return (
    <>
      <MetaTags
        title="So funktioniert MediQR - Notfall QR-Code erstellen"
        description="Erfahren Sie in 4 einfachen Schritten, wie Sie Ihren persönlichen Notfall-QR-Code mit MediQR erstellen und im Notfall Leben retten können."
        structuredData={getHowToSchema()}
      />
      <div className="min-h-screen bg-gradient-subtle">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-glow-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: "1s" }} />
        </div>
        
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-6">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Einfach und sicher</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-slide-up">
            So funktioniert <span className="gradient-text">MediQR</span>
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-in">
            In nur 3 einfachen Schritten zu Ihrem persönlichen Notfall-QR-Code
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="space-y-16">
            {/* Step 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <Card className="p-12 glass order-2 md:order-1 hover:shadow-glass transition-all duration-300">
                <div className="space-y-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white text-2xl font-bold shadow-glow">
                    1
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-4">Registrieren oder Anmelden</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      Erstellen Sie zunächst ein kostenloses Konto oder melden Sie sich an. Ihre Daten werden sicher verschlüsselt und sind nur für Sie zugänglich.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Kostenlose Registrierung</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Sichere Verschlüsselung</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Mehrere Profile möglich</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="w-64 h-64 rounded-3xl bg-gradient-primary/20 flex items-center justify-center animate-scale-in">
                  <Shield className="w-32 h-32 text-primary" />
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight className="w-12 h-12 text-primary animate-pulse" />
            </div>

            {/* Step 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="flex justify-center">
                <div className="w-64 h-64 rounded-3xl bg-gradient-primary/20 flex items-center justify-center animate-scale-in">
                  <Camera className="w-32 h-32 text-primary" />
                </div>
              </div>
              <Card className="p-12 glass hover:shadow-glass transition-all duration-300">
                <div className="space-y-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white text-2xl font-bold shadow-glow">
                    2
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-4">Profil erstellen</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      Fügen Sie alle wichtigen Informationen hinzu: Foto, Name, Geburtsdatum, Blutgruppe, Medikamente, Allergien und Notfallkontakte.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Persönliche Daten</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Medizinische Informationen</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Notfallkontakte</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight className="w-12 h-12 text-primary animate-pulse" />
            </div>

            {/* Step 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <Card className="p-12 glass order-2 md:order-1 hover:shadow-glass transition-all duration-300">
                <div className="space-y-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white text-2xl font-bold shadow-glow">
                    3
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-4">QR-Code drucken</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      Ihr personalisierter QR-Code wird automatisch generiert. Drucken Sie ihn aus und platzieren Sie ihn an gut sichtbaren Stellen.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Druckoptimiert</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Für Kühlschrank geeignet</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">Für Geldbeutel geeignet</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="w-64 h-64 rounded-3xl bg-gradient-primary/20 flex items-center justify-center animate-scale-in">
                  <Printer className="w-32 h-32 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Warum ist das <span className="gradient-text">wichtig</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Im Notfall können diese Informationen Leben retten
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 glass hover:shadow-glass transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mb-4">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Schneller Zugriff</h3>
              <p className="text-muted-foreground leading-relaxed">
                Rettungskräfte scannen einfach den QR-Code und haben sofort Zugriff auf alle wichtigen medizinischen Informationen - ohne lange Suche nach Dokumenten.
              </p>
            </Card>

            <Card className="p-8 glass hover:shadow-glass transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mb-4">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Lebensrettend</h3>
              <p className="text-muted-foreground leading-relaxed">
                Informationen über Allergien, Medikamente und Vorerkrankungen können im Ernstfall den Unterschied zwischen Leben und Tod bedeuten.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-subtle relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center space-y-8 p-12 rounded-3xl glass">
            <h2 className="text-4xl md:text-5xl font-bold">
              Bereit zu <span className="gradient-text">starten</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Erstellen Sie jetzt Ihren ersten MediQR-Code und schützen Sie sich und Ihre Liebsten im Notfall.
            </p>
            <Link to="/create">
              <Button size="lg" className="bg-gradient-primary hover:shadow-accent-glow transition-all duration-300 text-lg px-10 h-16">
                <QrCode className="mr-2 h-6 w-6" />
                Jetzt starten
              </Button>
            </Link>
            
            <div className="mt-8 pt-8 border-t border-border/50">
              <SocialShare
                title="So funktioniert MediQR - In 4 einfachen Schritten"
                description="Erfahre wie du in wenigen Minuten deinen persönlichen Notfall-QR-Code erstellst. Deine Gesundheitsinformationen, immer griffbereit für Rettungskräfte!"
                hashtags={["MediQR", "Notfallhilfe", "Tutorial", "Gesundheit"]}
              />
            </div>
          </div>
        </div>
      </section>

      </div>
    </>
  );
};

export default HowItWorks;
